import { useCallback, useEffect, useMemo, useState } from "react";
import { func, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useEstimateStructure, PercentSettingContents } from "@tools";
import { Accordion, Button } from "@components";
import { Grid, Switch } from "@material-ui/core";
import { Percent } from "@assets";

export const CellAutomation = observer(({
  workType,
  section,
  element,
  onSave,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();

  const [ localPercent, setPercent ] = useState(1);
  const [ automationUsed, setAutomationUsed ] = useState(false);
  const [ usedWorkTypes, setUsedWorkTypes ] = useState([]);
  const [ usedSections, setUsedSections ] = useState([]);
  
  const hasAutomation = Boolean(
    workType?.id
      ? element.workloadAutomation?.find(w => w.workTypeId === workType.id)
      : element.fixedPriceAutomation
  );

  const availableWorkTypes = useMemo(() => (
    workType
      ? structure.workTypes?.filter(workType => !workType.hasFixedValues)
      : []
  ), [ structure.workTypes, workType ]);

  const blacklistedWorkTypes = useMemo(() => (
    workType
      ? structure.blacklistedAutomationWorkTypesPerSection(workType.id)
      : []
  ), [ section?.usedWorkTypes, structure.sections ]);

  const disabled = useMemo(() => (
    automationUsed
      ? (workType && !usedWorkTypes.length) || !usedSections.length || typeof localPercent !== "number"
      : !hasAutomation
  ), [ automationUsed, usedWorkTypes, usedSections, localPercent, hasAutomation ]);

  const resetSettings = useCallback(() => {
    const usedSettings = workType?.id
      ? element.workloadAutomation?.find(w => w.workTypeId === workType.id)
      : element.fixedPriceAutomation;
    
    setPercent(usedSettings?.percent || 10);
    setAutomationUsed(usedSettings?.percent > 0);
    if(usedSettings?.ofWorkTypes)
      setUsedWorkTypes([ ...usedSettings.ofWorkTypes ]);
    if(usedSettings?.ofSections)
      setUsedSections([ ...usedSettings.ofSections ]);
  }, [ element, section, structure.sections, workType ]);

  useEffect(() => {
    resetSettings();
  }, [ resetSettings ]);

  const handleExpand = () => {
    setAutomationUsed((state) => !state);
  };

  const handleSave = () => {
    onSave({
      percent: automationUsed ? localPercent : 0,
      usedWorkTypes: automationUsed ? [ ...usedWorkTypes ] : [],
      usedSections: automationUsed ? [ ...usedSections ] : [],
      automationUsed,
    });
  };

  return (
    <>
      <Accordion
        expanded={automationUsed}
        hideExpandButton
        title={
          <Grid item container justifyContent="space-between" alignItems="center">
            <Grid item container wrap="nowrap" alignItems="center" className="w-max">
              <Percent className="color-primary text-2xl mr-2" />
              {t("views.editor.dialogs.automation.enable")}
            </Grid>
            <Switch
              color="primary"
              checked={automationUsed}
              onChange={handleExpand}
              name={
                automationUsed
                  ? t("views.editor.dialogs.automation.disable")
                  : t("views.editor.dialogs.automation.enable")
              }
            />
          </Grid>
        }
        variant="info"
        bordered={false}
        titleColored
      >
        <Grid item container direction="column" className="p-4 gap-3">
          <PercentSettingContents
            percent={localPercent}
            usedWorkTypes={usedWorkTypes}
            usedSections={usedSections}
            availableWorkTypes={availableWorkTypes}
            availableSections={structure.sections}
            blacklistedWorkTypes={blacklistedWorkTypes}
            onPercentChange={setPercent}
            onSelectWorkType={workType ? setUsedWorkTypes : undefined}
            onSelectSection={setUsedSections}
            editedWorkTypeName={workType?.name}
            allowSelectAllSections
            useSectionsOf={Boolean(workType)}
          />
        </Grid>
      </Accordion>
      <Grid item container justifyContent="flex-end" className="mt-2 gap-2">
        <Button
          variant="outlined"
          className="w-max"
          onClick={resetSettings}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          className="w-max"
          onClick={handleSave}
          disabled={disabled}
        >
          {t("common.apply")}
        </Button>
      </Grid>
    </>
  );
});

CellAutomation.propTypes = {
  section: object.isRequired,
  element: object.isRequired,
  onSave: func.isRequired,
  workType: object,
  onCancel: func,
};
